import { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, Search, MoreVertical, Edit, Trash2, Eye, Filter, AlertCircle,
  List, LayoutGrid, Phone, Mail, GripVertical, DollarSign, User, CheckCircle2, Tag, History, PlusCircle, Calendar, Download, AlertTriangle, X, MessageSquare, Video, Users, Settings, Ban, MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../context/PermissionsContext';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { US_STATES } from '../data/usStates';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { TagsDisplay, TagEditor, TagFilter } from '../components/TagComponents';
import ActivityHistoryModal from '../components/ActivityHistoryModal';
import ConsultationWizardModal from '../components/ConsultationWizardModal';
import EventRSVPWizardModal from '../components/EventRSVPWizardModal';
import { DownloadWizard } from '../components/ui/download-wizard';
import { LeadViewDialog } from '../components/leads/LeadViewDialog';
import { LeadQuickTaskModal } from '../components/leads/LeadQuickTaskModal';
import StageSettingsPanel from './StageSettingsPanel';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback stages used until dynamic settings load
const FALLBACK_STAGES = [
  'cold_data',
  'attempting_contact',
  'contacted',
  'qualified',
  'consultation_scheduled',
  'consultation_completed',
  'warm_follow_up',
  'rsvp_office_event',
  'attended_office_event',
  'closed_won',
  'closed_lost',
  'long_term_nurture'
];

const FALLBACK_STAGE_LABELS = {
  cold_data: 'Cold Data',
  attempting_contact: 'Attempting Contact',
  contacted: 'Contacted',
  qualified: 'Qualified',
  consultation_scheduled: 'Consultation Scheduled',
  consultation_completed: 'Consultation Completed',
  warm_follow_up: 'Warm Follow-Up',
  rsvp_office_event: 'RSVP Office Event',
  attended_office_event: 'Attended Office Event',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
  long_term_nurture: 'Long-Term Nurture'
};

const FALLBACK_TRANSITIONS = {
  cold_data: ['attempting_contact'],
  attempting_contact: ['contacted'],
  contacted: ['qualified'],
  qualified: ['consultation_scheduled', 'rsvp_office_event'],
  consultation_scheduled: ['consultation_completed', 'warm_follow_up'],
  consultation_completed: ['warm_follow_up', 'rsvp_office_event', 'closed_won'],
  rsvp_office_event: ['attended_office_event', 'warm_follow_up'],
  attended_office_event: ['warm_follow_up', 'closed_won'],
  warm_follow_up: ['consultation_scheduled', 'rsvp_office_event', 'closed_lost', 'qualified'],
  closed_lost: ['long_term_nurture'],
  long_term_nurture: ['qualified'],
  closed_won: [] // Terminal - no transitions
};

const SOURCES = ['Website', 'Referral', 'Social Media', 'Event', 'Cold Call', 'Advertisement', 'Other'];
const LEAD_SOURCES = ['Website Form', 'Meta Lead Ads', 'Event Registration', 'Field Data', 'Walk-in', 'Referral', 'Bulk Import', 'Old CRM Migration', 'Other'];
const PRODUCTS = ['Land Plot', 'Apartment Unit', 'Hotel Share', 'Not Sure'];

const stageColors = {
  cold_data: 'bg-slate-100 text-slate-700 border-slate-300',
  attempting_contact: 'bg-blue-100 text-blue-800 border-blue-300',
  contacted: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  qualified: 'bg-purple-100 text-purple-800 border-purple-300',
  consultation_scheduled: 'bg-cyan-100 text-cyan-800 border-cyan-300',
  consultation_completed: 'bg-teal-100 text-teal-800 border-teal-300',
  warm_follow_up: 'bg-amber-100 text-amber-800 border-amber-300',
  rsvp_office_event: 'bg-orange-100 text-orange-800 border-orange-300',
  attended_office_event: 'bg-lime-100 text-lime-800 border-lime-300',
  closed_won: 'bg-green-100 text-green-800 border-green-300',
  closed_lost: 'bg-red-100 text-red-700 border-red-300',
  long_term_nurture: 'bg-violet-100 text-violet-800 border-violet-300'
};

const stageHeaderColors = {
  cold_data: 'bg-slate-500',
  attempting_contact: 'bg-blue-500',
  contacted: 'bg-indigo-500',
  qualified: 'bg-purple-500',
  consultation_scheduled: 'bg-cyan-500',
  consultation_completed: 'bg-teal-500',
  warm_follow_up: 'bg-amber-500',
  rsvp_office_event: 'bg-orange-500',
  attended_office_event: 'bg-lime-600',
  closed_won: 'bg-green-500',
  closed_lost: 'bg-red-500',
  long_term_nurture: 'bg-violet-500'
};

// Stages that can be auto-transitioned (no notes required)
const AUTO_TRANSITION_STAGES = ['attended_office_event']; // Stages set via automation

// View preference storage key
const VIEW_STORAGE_KEY = 'goldsands_leads_view';

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [assignableUsers, setAssignableUsers] = useState([]); // Users who can own leads
  const [allTags, setAllTags] = useState([]); // All tags for display and filtering
  const [selectedTagFilters, setSelectedTagFilters] = useState([]); // Tag filter selection
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all'); // Lead source filter
  const [repFilter, setRepFilter] = useState('all'); // Owner/rep filter (non-AE only)
  const [sortBy, setSortBy] = useState('-created_at'); // Default: newest first
  const [leadSources, setLeadSources] = useState([]); // Available lead sources
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    return sessionStorage.getItem(VIEW_STORAGE_KEY) || 'list';
  });
  const [isUpdatingStage, setIsUpdatingStage] = useState(null); // Track which lead is being updated
  const [closedWonConfirmation, setClosedWonConfirmation] = useState(null); // For closed_won confirmation modal
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false); // Activity history modal
  const [activityLeadId, setActivityLeadId] = useState(null); // Lead for activity history
  const [activityLeadName, setActivityLeadName] = useState(''); // Lead name for modal title
  
  // Wizard modals state
  const [consultationWizard, setConsultationWizard] = useState({ open: false, lead: null });
  const [eventRSVPWizard, setEventRSVPWizard] = useState({ open: false, lead: null });
  
  // Stage update with notes modal
  const [stageUpdateModal, setStageUpdateModal] = useState({ open: false, leadId: null, newStage: null, previousStage: null });
  const [stageUpdateNotes, setStageUpdateNotes] = useState('');
  const [stageUpdateContactMode, setStageUpdateContactMode] = useState(''); // Mode of contact for stage change
  
  // Contact modes for KPI tracking
  const CONTACT_MODES = [
    { value: 'phone_call', label: 'Phone Call', icon: 'Phone' },
    { value: 'message', label: 'Message', icon: 'MessageSquare' },
    { value: 'email', label: 'Email', icon: 'Mail' },
    { value: 'video_call', label: 'Video Call', icon: 'Video' },
    { value: 'in_person', label: 'In Person', icon: 'Users' },
    { value: 'no_contact', label: 'No Contact', icon: 'Ban' }
  ];
  
  // Quick Task from Lead modal
  const [quickTaskModal, setQuickTaskModal] = useState({ open: false, lead: null });
  
  // Custom quick notes state
  const [userQuickNotes, setUserQuickNotes] = useState([]);
  const [newQuickNote, setNewQuickNote] = useState('');
  const [showQuickNoteInput, setShowQuickNoteInput] = useState(false);
  const [editingQuickNote, setEditingQuickNote] = useState(null);
  const [editQuickNoteText, setEditQuickNoteText] = useState('');
  
  // Download state (Owner only)
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadColumns, setDownloadColumns] = useState([]);
  
  // Duplicate check state
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [ignoredDuplicatePhone, setIgnoredDuplicatePhone] = useState(null); // Track phone that user chose to ignore
  
  const { hasPermission, role, user } = usePermissions();
  const isOwner = role === 'owner';
  const isAdmin = role === 'admin';

  // Dynamic stage settings
  const [stageConfig, setStageConfig] = useState(null);
  const [stageSettingsOpen, setStageSettingsOpen] = useState(false);
  const [opportunityModal, setOpportunityModal] = useState({ open: false, leadId: null, targetStage: null, previousStage: null });
  const [opportunityForm, setOpportunityForm] = useState({ deal_value: '', estimated_cash_collection: '', product_interest: '' });

  // Derived from stageConfig or fallbacks — memoized so child components don't re-render
  // on every parent render (also groups stages by category: lead → opportunity)
  const STAGES = useMemo(() => {
    if (!stageConfig) return FALLBACK_STAGES;
    const sorted = [...stageConfig.stages].sort((a, b) => {
      // Leads first, then opportunities, then anything else
      const catRank = (c) => c === 'lead' ? 0 : c === 'opportunity' ? 1 : 2;
      const ra = catRank(a.category);
      const rb = catRank(b.category);
      if (ra !== rb) return ra - rb;
      return (a.order ?? 0) - (b.order ?? 0);
    });
    return sorted.map(s => s.stage_id);
  }, [stageConfig]);

  const STAGE_LABELS = useMemo(() => {
    if (!stageConfig) return FALLBACK_STAGE_LABELS;
    return Object.fromEntries(stageConfig.stages.map(s => [s.stage_id, s.label]));
  }, [stageConfig]);

  const STAGE_TRANSITIONS = useMemo(() => {
    return stageConfig ? stageConfig.transitions : FALLBACK_TRANSITIONS;
  }, [stageConfig]);

  const stagesMap = useMemo(() => {
    return stageConfig
      ? Object.fromEntries(stageConfig.stages.map(s => [s.stage_id, s]))
      : {};
  }, [stageConfig]);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    source: '',
    lead_source: 'Other',
    stage: 'cold_data',
    product_interest: '',
    estimated_value: '',
    us_state: '',
    notes: '',
    assigned_to: '',
    contact_mode: ''
  });

  // Permission checks
  const canCreateLead = hasPermission('leads_create');
  const canDeleteLead = hasPermission('leads_delete');
  const canAssignLead = hasPermission('leads_assign');
  const canUpdateLead = hasPermission('leads_update_assigned');
  const canViewUsers = hasPermission('users_view');
  const isAccountExecutive = role === 'account_executive';
  const isTopManagement = role === 'top_management';
  const isFinance = role === 'finance';
  const isReadOnly = isTopManagement || isFinance;
  // Roles that must/can specify owner when creating leads
  const canSelectOwner = ['admin', 'owner', 'sales_manager'].includes(role);
  const mustSelectOwner = ['admin', 'owner'].includes(role);
  // Can create/manage tag definitions
  const canManageTags = ['admin', 'owner', 'sales_manager'].includes(role);

  // Lead download columns for owner export
  const LEAD_DOWNLOAD_COLUMNS = [
    { key: "first_name", label: "First Name" },
    { key: "last_name", label: "Last Name" },
    { key: "phone", label: "Phone" },
    { key: "email", label: "Email" },
    { key: "lead_source", label: "Lead Source" },
    { key: "stage", label: "Stage" },
    { key: "assigned_to", label: "Assigned To (ID)" },
    { key: "city", label: "City" },
    { key: "state", label: "State" },
    { key: "country", label: "Country" },
    { key: "notes", label: "Notes" },
    { key: "created_at", label: "Created At" },
    { key: "updated_at", label: "Updated At" }
  ];

  // Format phone number to US format
  const formatPhoneUS = (phone) => {
    if (!phone) return phone;
    const digits = phone.replace(/\D/g, '');
    // Remove country code if present (11 digits starting with 1)
    const normalizedDigits = digits.length === 11 && digits.startsWith('1') 
      ? digits.slice(1) 
      : digits;
    if (normalizedDigits.length === 10) {
      return `(${normalizedDigits.slice(0,3)}) ${normalizedDigits.slice(3,6)}-${normalizedDigits.slice(6)}`;
    }
    return phone;
  };

  // Check for duplicate phone number
  const checkDuplicate = async (phone, excludeLeadId = null) => {
    if (!phone || phone.replace(/\D/g, '').length < 10) {
      setDuplicateWarning(null);
      return;
    }
    
    // Skip check if user already chose to ignore this phone
    const normalizedPhone = phone.replace(/\D/g, '');
    if (ignoredDuplicatePhone && ignoredDuplicatePhone === normalizedPhone) {
      return;
    }
    
    setCheckingDuplicate(true);
    try {
      const response = await fetch(`${API_URL}/api/leads/check-duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          phone, 
          exclude_lead_id: excludeLeadId 
        }),
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.is_duplicate) {
          setDuplicateWarning({
            message: `This phone number already exists.`,
            existingLead: data.existing_lead,
            phone: normalizedPhone
          });
        } else {
          setDuplicateWarning(null);
          // Auto-format the phone number
          if (data.formatted_phone && data.formatted_phone !== phone) {
            handleFormChange('phone', data.formatted_phone);
          }
        }
      }
    } catch (error) {
      logger.error('Error checking duplicate:', error);
    } finally {
      setCheckingDuplicate(false);
    }
  };

  // Handle download
  const handleDownload = async (selectedColumns) => {
    try {
      const response = await fetch(`${API_URL}/api/leads/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          columns: selectedColumns,
          filters: { stage: stageFilter !== 'all' ? stageFilter : null }
        }),
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `leads_export_${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Download started');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Download failed');
      }
    } catch (error) {
      logger.error('Error downloading:', error);
      toast.error('Download failed');
    }
  };

  // Ignore duplicate - user acknowledges and wants to proceed anyway
  const ignoreDuplicate = () => {
    if (duplicateWarning?.phone) {
      setIgnoredDuplicatePhone(duplicateWarning.phone);
      toast.success('Duplicate ignored - you can proceed with this lead');
    }
    setDuplicateWarning(null);
  };

  // Clear duplicate warning (just dismisses UI without ignoring)
  const clearDuplicateWarning = () => setDuplicateWarning(null);

  // Save view mode preference
  const handleViewChange = (mode) => {
    setViewMode(mode);
    sessionStorage.setItem(VIEW_STORAGE_KEY, mode);
  };

  // Fetch users for owner display
  const fetchStageConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/stage-settings`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStageConfig(data);
      }
    } catch (err) {
      logger.error('Failed to load stage settings:', err);
    }
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
        // Create a map for quick lookup
        const map = {};
        data.forEach(user => {
          map[user.user_id] = user;
        });
        setUsersMap(map);
      }
    } catch (error) {
      logger.error('Error fetching users:', error);
    }
  };

  // Fetch assignable users (AEs and SMs who can own leads)
  const fetchAssignableUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/leads/assignable-users`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAssignableUsers(data);
      }
    } catch (error) {
      logger.error('Error fetching assignable users:', error);
    }
  };

  // Fetch all tags
  const fetchTags = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tags?include_inactive=true`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAllTags(data);
      }
    } catch (error) {
      logger.error('Error fetching tags:', error);
    }
  };

  // Fetch user's custom quick notes for lead stages
  const fetchUserQuickNotes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/quick-notes?category=lead_stage`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUserQuickNotes(data);
      }
    } catch (error) {
      logger.error('Error fetching quick notes:', error);
    }
  };

  // Create a new custom quick note
  const handleCreateQuickNote = async () => {
    if (!newQuickNote.trim()) return;
    
    try {
      const response = await fetch(`${API_URL}/api/quick-notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          note_text: newQuickNote.trim(),
          category: 'lead_stage'
        }),
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Quick note created');
        setNewQuickNote('');
        setShowQuickNoteInput(false);
        fetchUserQuickNotes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create quick note');
      }
    } catch (error) {
      logger.error('Error creating quick note:', error);
      toast.error('Failed to create quick note');
    }
  };

  // Delete a custom quick note
  const handleDeleteQuickNote = async (noteId) => {
    try {
      const response = await fetch(`${API_URL}/api/quick-notes/${noteId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Quick note deleted');
        fetchUserQuickNotes();
      } else {
        toast.error('Failed to delete quick note');
      }
    } catch (error) {
      logger.error('Error deleting quick note:', error);
      toast.error('Failed to delete quick note');
    }
  };

  // Edit an existing quick note
  const handleEditQuickNote = async (noteId) => {
    if (!editQuickNoteText.trim()) return;
    try {
      const response = await fetch(`${API_URL}/api/quick-notes/${noteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note_text: editQuickNoteText.trim() }),
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Quick note updated');
        setEditingQuickNote(null);
        setEditQuickNoteText('');
        fetchUserQuickNotes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update');
      }
    } catch {
      toast.error('Failed to update quick note');
    }
  };

  // Get owner name from user_id
  const getOwnerName = (userId, lead = null) => {
    if (lead?.assigned_to_name) return lead.assigned_to_name;
    if (!userId) return 'Unassigned';
    const user = usersMap[userId];
    return user?.name || 'Unknown';
  };

  // Get owner initials
  const getOwnerInitials = (userId) => {
    if (!userId) return '?';
    const user = usersMap[userId];
    if (!user || !user.name) return '?';
    const names = user.name.split(' ');
    return names.map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Check if user can edit tags for a specific lead
  const canEditLeadTags = (lead) => {
    if (isReadOnly) return false;
    if (isAccountExecutive) {
      // AE can only edit tags on their own leads
      return lead.assigned_to === usersMap[Object.keys(usersMap).find(k => usersMap[k]?.email)]?.user_id;
    }
    return canUpdateLead;
  };

  // Fetch available lead sources
  const fetchLeadSources = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/leads/sources`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setLeadSources((data.sources || []).filter(s => s && s.trim()));
      }
    } catch (error) {
      logger.error('Error fetching lead sources:', error);
    }
  }, []);

  const fetchLeads = useCallback(async () => {
    try {
      let url = `${API_URL}/api/leads?`;
      const params = new URLSearchParams();
      
      if (stageFilter !== 'all' && !stageFilter.startsWith('__')) params.append('stage', stageFilter);
      // For category filters, pass all stage IDs in that category
      if (stageFilter === '__lead_category__' && stageConfig) {
        stageConfig.stages.filter(s => s.category === 'lead').forEach(s => params.append('stage', s.stage_id));
      }
      if (stageFilter === '__opp_category__' && stageConfig) {
        stageConfig.stages.filter(s => s.category === 'opportunity').forEach(s => params.append('stage', s.stage_id));
      }
      if (sourceFilter !== 'all') params.append('source', sourceFilter);
      if (repFilter && repFilter !== 'all') {
        if (repFilter === '__unassigned__') {
          params.append('assigned_to', '__none__');
        } else {
          params.append('assigned_to', repFilter);
        }
      }
      if (sortBy) params.append('sort_by', sortBy);
      
      url += params.toString();
      
      const response = await fetch(url, { credentials: 'include' });
      
      if (response.ok) {
        const data = await response.json();
        setLeads(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      logger.error('Error fetching leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [stageFilter, sourceFilter, repFilter, sortBy, stageConfig]);

  // One-time data that doesn't depend on filters — fetch once on mount
  useEffect(() => {
    fetchUsers();
    fetchAssignableUsers();
    fetchStageConfig();
    fetchTags();
    fetchUserQuickNotes();
    fetchLeadSources();

    // Listen for tags-updated events
    const handleTagsUpdated = () => fetchTags();
    window.addEventListener('tags-updated', handleTagsUpdated);
    return () => window.removeEventListener('tags-updated', handleTagsUpdated);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter-driven refetch — leads only
  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Get allowed transitions for a stage
  const getAllowedTransitions = (currentStage) => {
    return STAGE_TRANSITIONS[currentStage] || [];
  };

  // Check if a transition is valid
  const isValidTransition = (fromStage, toStage) => {
    if (fromStage === toStage) return true;
    const allowed = getAllowedTransitions(fromStage);
    return allowed.includes(toStage);
  };

  // Check if moving from Lead to Opportunity category
  const isLeadToOpportunity = (fromStage, toStage) => {
    // Use dynamic stagesMap if available, otherwise check defaults
    if (stageConfig && stageConfig.stages.length > 0) {
      const fromInfo = stagesMap[fromStage];
      const toInfo = stagesMap[toStage];
      return fromInfo?.category === 'lead' && toInfo?.category === 'opportunity';
    }
    // Fallback: use default categorization
    const defaultLeadStages = ['cold_data', 'attempting_contact', 'contacted', 'qualified', 'long_term_nurture'];
    const defaultOppStages = ['consultation_scheduled', 'consultation_completed', 'warm_follow_up', 'rsvp_office_event', 'attended_office_event', 'closed_won', 'closed_lost'];
    return defaultLeadStages.includes(fromStage) && defaultOppStages.includes(toStage);
  };

  // Update lead stage - reusable for both views
  const updateLeadStage = async (leadId, newStage, previousStage) => {
    // Ensure stage config is loaded
    if (!stageConfig) {
      toast.error('Stage settings still loading, please try again');
      try {
        await fetchStageConfig();
      } catch (err) {
        logger.error('Failed to refetch stage config during updateLeadStage', err);
      }
      return;
    }

    // Validate transition
    if (!isValidTransition(previousStage, newStage)) {
      const allowedList = getAllowedTransitions(previousStage);
      toast.error(`Invalid transition from "${STAGE_LABELS[previousStage]}" to "${STAGE_LABELS[newStage]}". Allowed: ${allowedList.map(s => STAGE_LABELS[s]).join(', ') || 'none'}`);
      return;
    }

    const lead = leads.find(l => l.lead_id === leadId);

    // Check if this is a Lead → Opportunity transition that needs mandatory fields
    if (isLeadToOpportunity(previousStage, newStage)) {
      // Always check if opportunity fields are filled — show modal if any are missing
      const hasDealValue = lead?.deal_value && parseFloat(lead.deal_value) > 0;
      const hasProduct = lead?.product_interest && lead.product_interest !== '';
      const hasCashCollection = lead?.estimated_cash_collection && parseFloat(lead.estimated_cash_collection) > 0;
      if (!hasDealValue || !hasProduct || !hasCashCollection) {
        setOpportunityModal({ open: true, leadId, targetStage: newStage, previousStage });
        setOpportunityForm({
          deal_value: lead?.deal_value || '',
          estimated_cash_collection: lead?.estimated_cash_collection || '',
          product_interest: lead?.product_interest || '',
        });
        return;
      }
    }

    // If moving to consultation_scheduled, show consultation wizard
    if (newStage === 'consultation_scheduled') {
      setConsultationWizard({ open: true, lead });
      return;
    }

    // If moving to rsvp_office_event, show event RSVP wizard
    if (newStage === 'rsvp_office_event') {
      setEventRSVPWizard({ open: true, lead });
      return;
    }

    // If moving to closed_won, show confirmation
    if (newStage === 'closed_won') {
      setClosedWonConfirmation({ leadId, newStage, previousStage });
      return;
    }

    // For all other transitions, require notes (show modal)
    setStageUpdateModal({ open: true, leadId, newStage, previousStage });
    setStageUpdateNotes('');
  };

  // Execute the actual stage update with notes
  const executeStageUpdate = async (leadId, newStage, previousStage, notes = '', contactMode = '') => {
    setIsUpdatingStage(leadId);
    
    // Optimistic update
    setLeads(prev => prev.map(lead => 
      lead.lead_id === leadId ? { ...lead, stage: newStage } : lead
    ));

    try {
      const payload = { stage: newStage };
      if (notes && notes.trim()) {
        payload.notes = notes.trim();
      }
      if (contactMode) {
        payload.contact_mode = contactMode;
      }
      
      const response = await fetch(`${API_URL}/api/leads/${leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(`Stage updated to ${STAGE_LABELS[newStage] || newStage}`);
      } else {
        // Revert on failure
        setLeads(prev => prev.map(lead => 
          lead.lead_id === leadId ? { ...lead, stage: previousStage } : lead
        ));
        
        const error = await response.json();
        if (response.status === 403) {
          toast.error('Access denied: You cannot update this lead');
        } else {
          toast.error(error.detail || 'Failed to update stage');
        }
      }
    } catch (error) {
      // Revert on error
      setLeads(prev => prev.map(lead => 
        lead.lead_id === leadId ? { ...lead, stage: previousStage } : lead
      ));
      logger.error('Error updating stage:', error);
      toast.error('Failed to update stage');
    } finally {
      setIsUpdatingStage(null);
    }
  };

  // Handle opportunity fields submission
  const handleOpportunitySubmit = async () => {
    const { leadId, targetStage, previousStage } = opportunityModal;
    if (!opportunityForm.deal_value || !opportunityForm.product_interest || !opportunityForm.estimated_cash_collection) {
      toast.error('All opportunity fields are required');
      return;
    }
    // Save opportunity fields + entered_opportunity flag
    try {
      const res = await fetch(`${API_URL}/api/leads/${leadId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deal_value: parseFloat(opportunityForm.deal_value),
          estimated_cash_collection: parseFloat(opportunityForm.estimated_cash_collection),
          product_interest: opportunityForm.product_interest,
          entered_opportunity: true,
        }),
      });
      if (!res.ok) {
        toast.error('Failed to save opportunity fields');
        return;
      }
    } catch {
      toast.error('Failed to save opportunity fields');
      return;
    }

    // Update local lead state
    const updatedLead = {
      ...(leads.find(l => l.lead_id === leadId) || {}),
      deal_value: parseFloat(opportunityForm.deal_value),
      estimated_cash_collection: parseFloat(opportunityForm.estimated_cash_collection),
      product_interest: opportunityForm.product_interest,
      entered_opportunity: true,
    };
    setLeads(prev => prev.map(l => l.lead_id === leadId ? updatedLead : l));
    setOpportunityModal({ open: false, leadId: null, targetStage: null, previousStage: null });

    // Now directly proceed to the appropriate stage change flow
    // (skip the opportunity check since we just handled it)
    if (targetStage === 'consultation_scheduled') {
      setConsultationWizard({ open: true, lead: updatedLead });
    } else if (targetStage === 'rsvp_office_event') {
      setEventRSVPWizard({ open: true, lead: updatedLead });
    } else if (targetStage === 'closed_won') {
      setClosedWonConfirmation({ leadId, newStage: targetStage, previousStage });
    } else {
      // Show notes modal for all other stages
      setStageUpdateModal({ open: true, leadId, newStage: targetStage, previousStage });
      setStageUpdateNotes('');
    }
  };


  // Handle closed_won confirmation
  const handleClosedWonConfirm = async () => {
    if (closedWonConfirmation) {
      await executeStageUpdate(
        closedWonConfirmation.leadId,
        closedWonConfirmation.newStage,
        closedWonConfirmation.previousStage
      );
      setClosedWonConfirmation(null);
    }
  };

  // Handle stage update with notes confirmation
  const handleStageUpdateConfirm = async () => {
    if (!stageUpdateModal.leadId) return;
    
    if (!stageUpdateContactMode) {
      toast.error('Please select a mode of contact');
      return;
    }
    if (!stageUpdateNotes.trim()) {
      toast.error('Please add a note for this stage change');
      return;
    }
    
    await executeStageUpdate(
      stageUpdateModal.leadId,
      stageUpdateModal.newStage,
      stageUpdateModal.previousStage,
      stageUpdateNotes,
      stageUpdateContactMode
    );
    setStageUpdateModal({ open: false, leadId: null, newStage: null, previousStage: null });
    setStageUpdateNotes('');
    setStageUpdateContactMode('');
  };


  const handleCreateLead = async (e) => {
    e.preventDefault();
    
    // Validation: Admin/Owner must select an owner
    if (mustSelectOwner && !formData.assigned_to) {
      toast.error('Please select an owner for this lead');
      return;
    }
    
    try {
      const payload = {
        ...formData,
        // Convert empty strings to null for optional fields
        email: formData.email || null,
        last_name: formData.last_name || null,
        source: formData.source || null,
        notes: formData.notes || null,
        product_interest: formData.product_interest || null,
        estimated_value: formData.estimated_value ? parseFloat(formData.estimated_value) : null,
        us_state: formData.us_state || null,
        // Remove empty assigned_to to let backend handle default
        assigned_to: formData.assigned_to || undefined
      };

      const response = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Lead created successfully');
        setIsCreateOpen(false);
        resetForm();
        fetchLeads();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create lead');
      }
    } catch (error) {
      logger.error('Error creating lead:', error);
      toast.error('Failed to create lead');
    }
  };

  const handleUpdateLead = async (e) => {
    e.preventDefault();
    if (!formData.contact_mode) {
      toast.error('Please select a mode of contact');
      return;
    }
    try {
      // Send the full set of editable fields. AEs are now allowed full edit
      // access on their own leads (per user request); the only restriction
      // is reassignment, which the backend gates separately on
      // `leads_assign` permission.
      const payload = {
        first_name: formData.first_name || null,
        last_name: formData.last_name || null,
        email: formData.email || null,
        phone: formData.phone || null,
        source: formData.source || null,
        lead_source: formData.lead_source || null,
        stage: formData.stage,
        notes: formData.notes || null,
        contact_mode: formData.contact_mode,
        product_interest: formData.product_interest || null,
        estimated_value: formData.estimated_value ? parseFloat(formData.estimated_value) : null,
        us_state: formData.us_state || null,
      };
      // Only attach assigned_to if the user can reassign — otherwise we
      // would clear/overwrite ownership for AEs.
      if (!isAccountExecutive) {
        payload.assigned_to = formData.assigned_to || null;
      }

      const response = await fetch(`${API_URL}/api/leads/${selectedLead.lead_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Lead updated successfully');
        setIsEditOpen(false);
        resetForm();
        fetchLeads();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update lead');
      }
    } catch (error) {
      logger.error('Error updating lead:', error);
      toast.error('Failed to update lead');
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!confirm('Are you sure you want to delete this lead?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/leads/${leadId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Lead deleted successfully');
        fetchLeads();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete lead');
      }
    } catch (error) {
      logger.error('Error deleting lead:', error);
      toast.error('Failed to delete lead');
    }
  };

  const openEditDialog = (lead) => {
    setSelectedLead(lead);
    setFormData({
      first_name: lead.first_name,
      last_name: lead.last_name,
      email: lead.email || '',
      phone: lead.phone || '',
      source: lead.source || '',
      lead_source: lead.lead_source || 'Other',
      stage: lead.stage,
      product_interest: lead.product_interest || '',
      estimated_value: lead.estimated_value?.toString() || '',
      us_state: lead.us_state || '',
      notes: lead.notes || '',
      assigned_to: lead.assigned_to || '',
      contact_mode: ''  // Reset contact mode for each edit session
    });
    setIsEditOpen(true);
  };

  const openViewDialog = (lead) => {
    setSelectedLead(lead);
    setIsViewOpen(true);
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      source: '',
      lead_source: 'Other',
      stage: 'cold_data',
      product_interest: '',
      estimated_value: '',
      us_state: '',
      notes: '',
      assigned_to: '',
      contact_mode: ''
    });
    setSelectedLead(null);
    setDuplicateWarning(null);
    setIgnoredDuplicatePhone(null); // Reset ignored duplicate when form resets
  };

  // Handle lead tags updated
  const handleLeadTagsUpdated = (leadId, newTags) => {
    setLeads(prev => prev.map(lead => 
      lead.lead_id === leadId ? { ...lead, tags: newTags } : lead
    ));
  };

  const filteredLeads = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    return leads.filter(lead => {
      const matchesSearch = (
        lead.first_name?.toLowerCase().includes(searchLower) ||
        lead.last_name?.toLowerCase().includes(searchLower) ||
        lead.email?.toLowerCase().includes(searchLower) ||
        lead.phone?.includes(searchQuery)
      );

      // Tag filter (OR logic - lead must have ANY of the selected tags)
      const matchesTags = selectedTagFilters.length === 0 ||
        selectedTagFilters.some(tagId => lead.tags?.includes(tagId));

      return matchesSearch && matchesTags;
    });
  }, [leads, searchQuery, selectedTagFilters]);

  // Group leads by stage for Kanban view (memoized to avoid recomputing on every render)
  const leadsByStage = useMemo(() => {
    return STAGES.reduce((acc, stage) => {
      acc[stage] = filteredLeads.filter(lead => lead.stage === stage);
      return acc;
    }, {});
  }, [STAGES, filteredLeads]);

  // Handle drag end for Kanban
  const handleDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    // No destination or dropped in same place
    if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
      return;
    }

    const leadId = draggableId;
    const newStage = destination.droppableId;
    const previousStage = source.droppableId;

    // Update stage via API
    updateLeadStage(leadId, newStage, previousStage);
  };

  // Render Lead Form
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  // Create form content - memoized to prevent focus loss on re-render
  const createFormContent = (onSubmit, submitLabel, isEditMode = false) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="first_name">First Name *</Label>
          <Input
            id="first_name"
            value={formData.first_name}
            onChange={(e) => handleFormChange('first_name', e.target.value)}
            required
            className="input-goldsands"
            data-testid="lead-first-name"
          />
        </div>
        <div>
          <Label htmlFor="last_name">Last Name *</Label>
          <Input
            id="last_name"
            value={formData.last_name}
            onChange={(e) => handleFormChange('last_name', e.target.value)}
            required
            className="input-goldsands"
            data-testid="lead-last-name"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleFormChange('email', e.target.value)}
            disabled={false}
            className="input-goldsands"
            data-testid="lead-email"
          />
        </div>
        <div>
          <Label htmlFor="phone">Phone</Label>
          <div className="relative">
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => {
                const formatted = formatPhoneUS(e.target.value);
                handleFormChange('phone', formatted);
              }}
              onBlur={(e) => {
                if (!isEditMode && e.target.value) {
                  checkDuplicate(e.target.value);
                } else if (isEditMode && e.target.value && selectedLead?.phone !== e.target.value) {
                  checkDuplicate(e.target.value, selectedLead?.lead_id);
                }
              }}
              disabled={false}
              className={`input-goldsands ${duplicateWarning ? 'border-amber-400 focus:ring-amber-400' : ''}`}
              data-testid="lead-phone"
            />
            {checkingDuplicate && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gold"></div>
              </div>
            )}
          </div>
          {duplicateWarning && (
            <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-sm text-amber-800">Duplicate phone detected</p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Existing lead: <strong>{duplicateWarning.existingLead?.first_name} {duplicateWarning.existingLead?.last_name || ''}</strong>
                    {duplicateWarning.existingLead?.stage && ` (${STAGE_LABELS[duplicateWarning.existingLead.stage] || duplicateWarning.existingLead.stage})`}
                  </p>
                  <div className="flex gap-2 mt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs bg-white border-amber-300 text-amber-800 hover:bg-amber-100"
                      onClick={ignoreDuplicate}
                      data-testid="ignore-duplicate-btn"
                    >
                      <X size={12} className="mr-1" />
                      Ignore & Proceed
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="lead_source">Lead Source <span className="text-red-500">*</span></Label>
          <Select 
            value={formData.lead_source} 
            onValueChange={(v) => handleFormChange('lead_source', v)}
            disabled={false}
          >
            <SelectTrigger data-testid="lead-lead-source">
              <SelectValue placeholder="Select lead source" />
            </SelectTrigger>
            <SelectContent>
              {LEAD_SOURCES.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="stage">Stage</Label>
          <Select value={formData.stage} onValueChange={(v) => handleFormChange('stage', v)}>
            <SelectTrigger data-testid="lead-stage">
              <SelectValue placeholder="Select stage" />
            </SelectTrigger>
            <SelectContent>
              {isEditMode ? (
                // In edit mode, show current stage + allowed transitions
                <>
                  {formData.stage && (
                    <SelectItem value={formData.stage}>
                      {STAGE_LABELS[formData.stage] || formData.stage} (current)
                    </SelectItem>
                  )}
                  {getAllowedTransitions(selectedLead?.stage || formData.stage).map(s => (
                    <SelectItem key={s} value={s}>{STAGE_LABELS[s] || s}</SelectItem>
                  ))}
                </>
              ) : (
                // In create mode, show all stages up to "Warm Follow-Up" for flexibility
                // This allows importing leads at various stages
                <>
                  {STAGES.filter(s => !['closed_won', 'closed_lost', 'disqualified'].includes(s)).map(s => (
                    <SelectItem key={s} value={s}>{STAGE_LABELS[s] || s}</SelectItem>
                  ))}
                </>
              )}
            </SelectContent>
          </Select>
          {isEditMode && (
            <p className="text-xs text-slate-500 mt-1">
              Only valid stage transitions are shown
            </p>
          )}
          {!isEditMode && (
            <p className="text-xs text-slate-500 mt-1">
              Default is "Cold Data". Select different stage for leads from other sources.
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="product_interest">Product Interest</Label>
          <Select 
            value={formData.product_interest} 
            onValueChange={(v) => handleFormChange('product_interest', v)}
            disabled={false}
          >
            <SelectTrigger data-testid="lead-product">
              <SelectValue placeholder="Select product" />
            </SelectTrigger>
            <SelectContent>
              {PRODUCTS.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="estimated_value">Estimated Value ($)</Label>
          <Input
            id="estimated_value"
            type="number"
            value={formData.estimated_value}
            onChange={(e) => handleFormChange('estimated_value', e.target.value)}
            disabled={false}
            className="input-goldsands"
            data-testid="lead-value"
          />
        </div>
      </div>

      {/* US State (optional) */}
      <div>
        <Label htmlFor="us_state" className="flex items-center gap-2">
          <MapPin className="w-4 h-4" />
          US State <span className="text-xs font-normal text-slate-400">(optional)</span>
        </Label>
        <Select
          value={formData.us_state || '__none__'}
          onValueChange={(v) => handleFormChange('us_state', v === '__none__' ? '' : v)}
        >
          <SelectTrigger className="input-goldsands" data-testid="lead-create-us-state">
            <SelectValue placeholder="Select a US state…" />
          </SelectTrigger>
          <SelectContent className="max-h-80">
            <SelectItem value="__none__"><span className="text-slate-400">— Not specified —</span></SelectItem>
            {US_STATES.map(s => (
              <SelectItem key={s.code} value={s.code}>{s.name} ({s.code})</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Owner Assignment - shown for admin/owner/sales_manager */}
      {canSelectOwner && (
        <div>
          <Label htmlFor="assigned_to" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            Assign To {mustSelectOwner && !isEditMode && <span className="text-red-500">*</span>}
          </Label>
          <Select 
            value={formData.assigned_to || ''} 
            onValueChange={(v) => handleFormChange('assigned_to', v)}
            required={mustSelectOwner && !isEditMode}
          >
            <SelectTrigger data-testid="lead-assigned-to">
              <SelectValue placeholder="Select owner..." />
            </SelectTrigger>
            <SelectContent>
              {assignableUsers.map(user => (
                <SelectItem key={user.user_id} value={user.user_id}>
                  {user.name} ({user.role === 'account_executive' ? 'AE' : 'SM'})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500 mt-1">
            {isEditMode 
              ? 'Transfer this lead to another team member'
              : mustSelectOwner 
                ? 'Required: Select who will own this lead'
                : 'Optional: Leave empty to assign to yourself'}
          </p>
        </div>
      )}

      <div>
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => handleFormChange('notes', e.target.value)}
          className="input-goldsands min-h-[80px]"
          data-testid="lead-notes"
        />
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={() => { setIsCreateOpen(false); setIsEditOpen(false); resetForm(); }}>
          Cancel
        </Button>
        <Button type="submit" className="btn-accent" data-testid="lead-submit">
          {submitLabel}
        </Button>
      </div>
    </form>
  );

  // Render Lead Card for Kanban
  const LeadCard = ({ lead, index }) => (
    <Draggable draggableId={lead.lead_id} index={index} isDragDisabled={isReadOnly}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`bg-white rounded-lg border shadow-sm p-3 mb-2 transition-shadow ${
            snapshot.isDragging ? 'shadow-lg ring-2 ring-gold/50' : 'hover:shadow-md'
          } ${isUpdatingStage === lead.lead_id ? 'opacity-50' : ''}`}
          data-testid={`lead-card-${lead.lead_id}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              {/* Name */}
              <div className="flex items-center gap-2">
                <div
                  {...provided.dragHandleProps}
                  className="cursor-grab text-slate-400 hover:text-slate-600"
                >
                  <GripVertical size={14} />
                </div>
                <p className="font-medium text-slate-900 truncate">
                  {lead.first_name} {lead.last_name}
                </p>
              </div>
              
              {/* Contact Info */}
              <div className="mt-2 space-y-1 text-xs text-slate-500">
                {lead.phone && (
                  <div className="flex items-center gap-1">
                    <Phone size={10} />
                    <span className="truncate">{lead.phone}</span>
                  </div>
                )}
                {lead.email && (
                  <div className="flex items-center gap-1">
                    <Mail size={10} />
                    <span className="truncate">{lead.email}</span>
                  </div>
                )}
              </div>

              {/* Source & Product */}
              <div className="mt-2 flex flex-wrap gap-1">
                {lead.source && (
                  <Badge variant="outline" className="text-xs py-0 px-1.5">
                    {lead.source}
                  </Badge>
                )}
              </div>

              {/* Tags */}
              <div className="mt-2">
                <div className="flex items-center gap-1 flex-wrap">
                  <TagsDisplay 
                    tags={lead.tags || []} 
                    allTags={allTags} 
                    maxVisible={3}
                    small
                  />
                  {!isReadOnly && (
                    <TagEditor
                      leadId={lead.lead_id}
                      currentTags={lead.tags || []}
                      allTags={allTags}
                      onTagsUpdated={(newTags) => handleLeadTagsUpdated(lead.lead_id, newTags)}
                      disabled={false}
                      canCreateTag={canManageTags}
                    />
                  )}
                </div>
              </div>

              {/* Value */}
              {lead.estimated_value != null && lead.estimated_value !== '' && (
                <div className="mt-2 flex items-center gap-1 text-sm font-medium text-green-600">
                  <DollarSign size={12} />
                  {Number(lead.estimated_value).toLocaleString()}
                </div>
              )}

              {/* Owner */}
              <div className="mt-2 flex items-center gap-1.5">
                <Avatar className="h-5 w-5">
                  <AvatarFallback className="text-[10px] bg-slate-200 text-slate-600">
                    {getOwnerInitials(lead.assigned_to)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs text-slate-500 truncate">
                  {getOwnerName(lead.assigned_to, lead)}
                </span>
              </div>
            </div>

            {/* Actions */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0">
                  <MoreVertical size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openViewDialog(lead)}>
                  <Eye size={14} className="mr-2" /> View
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => {
                  setActivityLeadId(lead.lead_id);
                  setActivityLeadName(`${lead.first_name} ${lead.last_name || ''}`.trim());
                  setIsActivityModalOpen(true);
                }}>
                  <History size={14} className="mr-2" /> Activity History
                </DropdownMenuItem>
                {canUpdateLead && !isReadOnly && (
                  <DropdownMenuItem onClick={() => openEditDialog(lead)}>
                    <Edit size={14} className="mr-2" /> {isAccountExecutive ? 'Update' : 'Edit'}
                  </DropdownMenuItem>
                )}
                {canUpdateLead && !isReadOnly && (
                  <DropdownMenuItem onClick={() => {
                    setQuickTaskModal({ open: true, lead });
                  }}>
                    <PlusCircle size={14} className="mr-2" /> Add Task
                  </DropdownMenuItem>
                )}
                {canDeleteLead && (
                  <DropdownMenuItem 
                    onClick={() => handleDeleteLead(lead.lead_id)}
                    className="text-red-600"
                  >
                    <Trash2 size={14} className="mr-2" /> Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}
    </Draggable>
  );

  // Render Kanban Column
  const KanbanColumn = ({ stage, leads }) => {
    const stageInfo = stagesMap[stage];
    // Prefer the stage-specific color from settings; fall back to the static class map
    // for built-in stages; final fallback picks a neutral by category so custom stages
    // never render an invisible (colorless) header.
    const staticClass = stageHeaderColors[stage];
    const categoryFallback = stageInfo?.category === 'opportunity'
      ? 'bg-purple-500'
      : stageInfo?.category === 'lead'
      ? 'bg-blue-500'
      : 'bg-slate-500';
    const headerBg = stageInfo?.color || null;
    return (
      <div className="flex-shrink-0 w-72">
        <div
          className={`rounded-t-lg px-3 py-2 ${headerBg ? '' : (staticClass || categoryFallback)}`}
          style={headerBg ? { backgroundColor: headerBg } : undefined}
        >
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-white text-sm truncate">{STAGE_LABELS[stage] || stage}</h3>
            <Badge className="bg-white/20 text-white hover:bg-white/30 flex-shrink-0 ml-2">
              {leads.length}
            </Badge>
          </div>
        </div>
        <Droppable droppableId={stage}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`min-h-[400px] max-h-[calc(100vh-320px)] overflow-y-auto p-2 rounded-b-lg border border-t-0 ${
                snapshot.isDraggingOver ? 'bg-gold/5 border-gold' : 'bg-slate-50 border-slate-200'
              }`}
            >
              {leads.map((lead, index) => (
                <LeadCard key={lead.lead_id} lead={lead} index={index} />
              ))}
              {provided.placeholder}
              {leads.length === 0 && (
                <div className="text-center py-8 text-slate-400 text-sm">
                  No leads
                </div>
              )}
            </div>
          )}
        </Droppable>
      </div>
    );
  };

  // Stages to show based on filter
  const stagesToShow = (() => {
    if (stageFilter === 'all') return STAGES;
    if (stageFilter === '__lead_category__' && stageConfig) {
      return stageConfig.stages.filter(s => s.category === 'lead').map(s => s.stage_id);
    }
    if (stageFilter === '__opp_category__' && stageConfig) {
      return stageConfig.stages.filter(s => s.category === 'opportunity').map(s => s.stage_id);
    }
    if (stageFilter.startsWith('__')) return STAGES; // safety fallback
    return [stageFilter];
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 animate-fade-in" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Leads</h1>
          <p className="text-slate-500 font-body text-sm mt-1">
            {isAccountExecutive ? 'View and update your assigned leads' : 'Manage your sales leads'}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {(isOwner || isAdmin) && (
            <Button variant="outline" size="sm" className="h-8" onClick={() => setStageSettingsOpen(true)} data-testid="stage-settings-btn" title="Stage Settings">
              <Settings size={14} />
            </Button>
          )}
          {/* View Toggle */}
          <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => handleViewChange('list')}
              className={`h-7 px-2 text-xs ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
              data-testid="view-list-btn"
            >
              <List size={14} className="mr-1" />
              List
            </Button>
            <Button
              variant={viewMode === 'board' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => handleViewChange('board')}
              className={`h-7 px-2 text-xs ${viewMode === 'board' ? 'bg-white shadow-sm' : ''}`}
              data-testid="view-board-btn"
            >
              <LayoutGrid size={14} className="mr-1" />
              Board
            </Button>
          </div>

          {/* Download Button - Owner Only */}
          {isOwner && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDownloadOpen(true)}
              className="h-7 px-3 text-xs"
              data-testid="download-leads-btn"
            >
              <Download size={14} className="mr-1" />
              Download
            </Button>
          )}

          {canCreateLead && (
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button className="btn-accent h-7 px-3 text-xs" data-testid="create-lead-btn">
                  <Plus size={14} className="mr-1" />
                  Add Lead
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">Create New Lead</DialogTitle>
                </DialogHeader>
                {createFormContent(handleCreateLead, "Create Lead", false)}
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Account Executive notice */}
      {isAccountExecutive && (
        <Alert className="bg-blue-50 border-blue-200 py-2">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800 text-xs">
            You are viewing leads assigned to you. You can update all fields on your leads.
          </AlertDescription>
        </Alert>
      )}

      {/* Lead / Opportunity Category Tabs */}
      {stageConfig && (
        <div className="flex flex-wrap gap-1 bg-slate-100 rounded-lg p-1 w-fit max-w-full" data-testid="category-tabs">
          <button
            onClick={() => setStageFilter('all')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
              stageFilter === 'all' ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-all"
          >All</button>
          <button
            onClick={() => setStageFilter('__lead_category__')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
              stageFilter === '__lead_category__' ? 'bg-blue-600 text-white shadow' : 'text-blue-600 hover:bg-blue-50'
            }`}
            data-testid="tab-leads"
          >Leads ({stageConfig.stages.filter(s => s.category === 'lead').length})</button>
          <button
            onClick={() => setStageFilter('__opp_category__')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
              stageFilter === '__opp_category__' ? 'bg-purple-600 text-white shadow' : 'text-purple-600 hover:bg-purple-50'
            }`}
            data-testid="tab-opportunities"
          >Opportunity ({stageConfig.stages.filter(s => s.category === 'opportunity').length})</button>
        </div>
      )}

      {/* Filters - Compact utility bar */}
      <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center bg-white border rounded-lg px-2 py-1.5 shadow-sm">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
          <Input
            placeholder="Search leads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-7 h-7 text-xs border-slate-200"
            data-testid="search-leads"
          />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-full md:w-40 h-7 text-xs" data-testid="filter-stage">
            <Filter size={12} className="mr-1" />
            <SelectValue placeholder="Filter by stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {stageConfig && (
              <>
                <SelectItem value="__lead__" disabled className="text-xs font-semibold text-blue-600 opacity-100">— Lead Stages —</SelectItem>
                {stageConfig.stages.filter(s => s.category === 'lead').map(s => (
                  <SelectItem key={s.stage_id} value={s.stage_id}>{s.label}</SelectItem>
                ))}
                <SelectItem value="__opp__" disabled className="text-xs font-semibold text-purple-600 opacity-100">— Opportunity Stages —</SelectItem>
                {stageConfig.stages.filter(s => s.category === 'opportunity').map(s => (
                  <SelectItem key={s.stage_id} value={s.stage_id}>{s.label}</SelectItem>
                ))}
              </>
            )}
            {!stageConfig && STAGES.map(s => (
              <SelectItem key={s} value={s}>{STAGE_LABELS[s] || s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* Source Filter */}
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-full md:w-40 h-7 text-xs" data-testid="filter-source">
            <Filter size={12} className="mr-1" />
            <SelectValue placeholder="Filter by source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {leadSources.map(src => (
              <SelectItem key={src} value={src}>{src}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Rep Filter — hidden for Account Executives (they already see only their own leads) */}
        {!isAccountExecutive && (
          <Select value={repFilter} onValueChange={setRepFilter}>
            <SelectTrigger className="w-full md:w-48 h-7 text-xs" data-testid="filter-rep">
              <Filter size={12} className="mr-1" />
              <SelectValue placeholder="Filter by rep" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Reps</SelectItem>
              <SelectItem value="__unassigned__">— Unassigned —</SelectItem>
              {assignableUsers.map(u => (
                <SelectItem key={u.user_id} value={u.user_id}>
                  {u.name}{u.role ? ` · ${u.role}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        
        {/* Sort Options */}
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full md:w-44 h-7 text-xs" data-testid="sort-leads">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="-created_at">Newest First</SelectItem>
            <SelectItem value="created_at">Oldest First</SelectItem>
            <SelectItem value="source">Source (A-Z)</SelectItem>
            <SelectItem value="-source">Source (Z-A)</SelectItem>
          </SelectContent>
        </Select>
        
        {/* Tag Filter */}
        <TagFilter
          selectedTags={selectedTagFilters}
          allTags={allTags}
          onSelectionChange={setSelectedTagFilters}
          className="h-7"
        />
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <Card className="card-goldsands">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      No leads found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLeads.map((lead) => (
                    <TableRow key={lead.lead_id} className="table-row-hover">
                      <TableCell className="font-medium">
                        {lead.first_name} {lead.last_name}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {lead.email && <p>{lead.email}</p>}
                          {lead.phone && <p className="text-slate-500">{lead.phone}</p>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarFallback className="text-xs bg-slate-200 text-slate-600">
                              {getOwnerInitials(lead.assigned_to)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm text-slate-700">{getOwnerName(lead.assigned_to, lead)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {/* Tags display and editor */}
                        <div className="flex items-center gap-2">
                          <TagsDisplay 
                            tags={lead.tags || []} 
                            allTags={allTags} 
                            maxVisible={2}
                            small
                          />
                          {!isReadOnly && (
                            <TagEditor
                              leadId={lead.lead_id}
                              currentTags={lead.tags || []}
                              allTags={allTags}
                              onTagsUpdated={(newTags) => handleLeadTagsUpdated(lead.lead_id, newTags)}
                              disabled={false}
                              canCreateTag={canManageTags}
                            />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{lead.source || '-'}</TableCell>
                      <TableCell>
                        {/* Clickable Stage Dropdown - Only allowed transitions */}
                        {canUpdateLead && !isReadOnly && lead.stage ? (
                          <Select 
                            value={lead.stage} 
                            onValueChange={(newStage) => updateLeadStage(lead.lead_id, newStage, lead.stage)}
                            disabled={isUpdatingStage === lead.lead_id || getAllowedTransitions(lead.stage).length === 0}
                          >
                            <SelectTrigger 
                              className={`w-40 h-7 text-xs ${stageColors[lead.stage] || ''} border`}
                              data-testid={`stage-select-${lead.lead_id}`}
                            >
                              <SelectValue>{STAGE_LABELS[lead.stage] || lead.stage}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={lead.stage} className="text-xs">
                                {STAGE_LABELS[lead.stage] || lead.stage} (current)
                              </SelectItem>
                              {getAllowedTransitions(lead.stage).map(s => (
                                <SelectItem key={s} value={s} className="text-xs">
                                  {STAGE_LABELS[s]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge className={`${stageColors[lead.stage]}`}>
                            {STAGE_LABELS[lead.stage] || lead.stage}
                          </Badge>
                        )}
                        {lead.finance_setup_pending && (
                          <Badge className="ml-1 bg-amber-100 text-amber-800 border-amber-300 text-xs">
                            Finance Setup Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {lead.estimated_value != null ? `$${Number(lead.estimated_value).toLocaleString()}` : '-'}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`lead-actions-${lead.lead_id}`}>
                              <MoreVertical size={16} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openViewDialog(lead)}>
                              <Eye size={16} className="mr-2" /> View
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setActivityLeadId(lead.lead_id);
                              setActivityLeadName(`${lead.first_name} ${lead.last_name || ''}`.trim());
                              setIsActivityModalOpen(true);
                            }}>
                              <History size={16} className="mr-2" /> Activity History
                            </DropdownMenuItem>
                            {canUpdateLead && !isReadOnly && (
                              <DropdownMenuItem onClick={() => openEditDialog(lead)}>
                                <Edit size={16} className="mr-2" /> {isAccountExecutive ? 'Update' : 'Edit'}
                              </DropdownMenuItem>
                            )}
                            {canUpdateLead && !isReadOnly && (
                              <DropdownMenuItem onClick={() => {
                                setQuickTaskModal({ open: true, lead });
                              }}>
                                <PlusCircle size={16} className="mr-2" /> Add Task
                              </DropdownMenuItem>
                            )}
                            {canDeleteLead && (
                              <DropdownMenuItem 
                                onClick={() => handleDeleteLead(lead.lead_id)}
                                className="text-red-600"
                              >
                                <Trash2 size={16} className="mr-2" /> Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Board (Kanban) View */}
      {viewMode === 'board' && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="flex gap-4 overflow-x-auto pb-4 items-start" data-testid="kanban-board">
            {(() => {
              // Group stages by category so we can visually separate
              // LEAD stages from OPPORTUNITY stages (only when the user is
              // viewing "All", otherwise render a single flat row).
              const shouldGroup = stageFilter === 'all';
              if (!shouldGroup) {
                return stagesToShow.map(stage => (
                  <KanbanColumn key={stage} stage={stage} leads={leadsByStage[stage] || []} />
                ));
              }
              const leadStages = stagesToShow.filter(s => stagesMap[s]?.category === 'lead');
              const oppStages = stagesToShow.filter(s => stagesMap[s]?.category === 'opportunity');
              const otherStages = stagesToShow.filter(s => {
                const c = stagesMap[s]?.category;
                return c !== 'lead' && c !== 'opportunity';
              });
              const sections = [
                { key: 'lead', label: 'Lead Stages', accent: 'bg-blue-500', stages: leadStages },
                { key: 'opportunity', label: 'Opportunity Stages', accent: 'bg-purple-500', stages: oppStages },
              ];
              if (otherStages.length) {
                sections.push({ key: 'other', label: 'Other', accent: 'bg-slate-500', stages: otherStages });
              }
              return sections
                .filter(s => s.stages.length > 0)
                .map((section, idx) => (
                  <div key={section.key} className="flex items-start flex-shrink-0" data-testid={`kanban-group-${section.key}`}>
                    {/* Section wrapper with a subtle background tint */}
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2 px-1 mb-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${section.accent}`} />
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                          {section.label}
                        </span>
                        <span className="text-[10px] text-slate-400">
                          · {section.stages.length} stages
                        </span>
                      </div>
                      <div className={`flex gap-4 p-2 rounded-xl ${
                        section.key === 'lead'
                          ? 'bg-blue-50/60 border border-blue-100'
                          : section.key === 'opportunity'
                          ? 'bg-purple-50/60 border border-purple-100'
                          : 'bg-slate-50/60 border border-slate-100'
                      }`}>
                        {section.stages.map(stage => (
                          <KanbanColumn key={stage} stage={stage} leads={leadsByStage[stage] || []} />
                        ))}
                      </div>
                    </div>
                    {/* Divider between groups */}
                    {idx < sections.filter(s => s.stages.length > 0).length - 1 && (
                      <div className="h-[calc(100vh-280px)] w-px bg-slate-200 mx-3 mt-6 flex-shrink-0" />
                    )}
                  </div>
                ));
            })()}
          </div>
        </DragDropContext>
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {isAccountExecutive ? 'Update Lead' : 'Edit Lead'}
            </DialogTitle>
          </DialogHeader>
          {/* Inline form to prevent re-render issues with nested component */}
          <form onSubmit={handleUpdateLead} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_first_name">First Name *</Label>
                <Input
                  id="edit_first_name"
                  value={formData.first_name}
                  onChange={(e) => handleFormChange('first_name', e.target.value)}
                  required
                  className="input-goldsands"
                  data-testid="lead-first-name"
                />
              </div>
              <div>
                <Label htmlFor="edit_last_name">Last Name *</Label>
                <Input
                  id="edit_last_name"
                  value={formData.last_name}
                  onChange={(e) => handleFormChange('last_name', e.target.value)}
                  required
                  className="input-goldsands"
                  data-testid="lead-last-name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_email">Email</Label>
                <Input
                  id="edit_email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  className="input-goldsands"
                  data-testid="lead-email"
                />
              </div>
              <div>
                <Label htmlFor="edit_phone">Phone</Label>
                <Input
                  id="edit_phone"
                  value={formData.phone}
                  onChange={(e) => handleFormChange('phone', e.target.value)}
                  className="input-goldsands"
                  data-testid="lead-phone"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_lead_source">Lead Source <span className="text-red-500">*</span></Label>
                <Select 
                  value={formData.lead_source} 
                  onValueChange={(v) => handleFormChange('lead_source', v)}
                >
                  <SelectTrigger data-testid="lead-lead-source">
                    <SelectValue placeholder="Select lead source" />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_SOURCES.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit_stage">Stage</Label>
                <Select value={formData.stage} onValueChange={(v) => handleFormChange('stage', v)}>
                  <SelectTrigger data-testid="lead-stage">
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.stage && (
                      <SelectItem value={formData.stage}>
                        {STAGE_LABELS[formData.stage] || formData.stage} (current)
                      </SelectItem>
                    )}
                    {getAllowedTransitions(selectedLead?.stage || formData.stage).map(s => (
                      <SelectItem key={s} value={s}>{STAGE_LABELS[s] || s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Only valid stage transitions are shown
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_product_interest">Product Interest</Label>
                <Select 
                  value={formData.product_interest || ''} 
                  onValueChange={(v) => handleFormChange('product_interest', v)}
                >
                  <SelectTrigger data-testid="lead-product">
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRODUCTS.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit_estimated_value">Estimated Value ($)</Label>
                <Input
                  id="edit_estimated_value"
                  type="number"
                  value={formData.estimated_value}
                  onChange={(e) => handleFormChange('estimated_value', e.target.value)}
                  className="input-goldsands"
                  data-testid="lead-value"
                />
              </div>
            </div>

            {/* US State (optional) */}
            <div>
              <Label htmlFor="edit_us_state" className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                US State <span className="text-xs font-normal text-slate-400">(optional)</span>
              </Label>
              <Select
                value={formData.us_state || '__none__'}
                onValueChange={(v) => handleFormChange('us_state', v === '__none__' ? '' : v)}
              >
                <SelectTrigger className="input-goldsands" data-testid="lead-us-state">
                  <SelectValue placeholder="Select a US state…" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="__none__"><span className="text-slate-400">— Not specified —</span></SelectItem>
                  {US_STATES.map(s => (
                    <SelectItem key={s.code} value={s.code} data-testid={`us-state-option-${s.code}`}>
                      {s.name} ({s.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Owner Assignment - shown for admin/owner/sales_manager */}
            {canSelectOwner && (
              <div>
                <Label htmlFor="edit_assigned_to" className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Assign To
                </Label>
                <Select 
                  value={formData.assigned_to || ''} 
                  onValueChange={(v) => handleFormChange('assigned_to', v)}
                >
                  <SelectTrigger data-testid="lead-assigned-to">
                    <SelectValue placeholder="Select owner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {assignableUsers.map(user => (
                      <SelectItem key={user.user_id} value={user.user_id}>
                        {user.name} ({user.role === 'account_executive' ? 'AE' : 'SM'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Transfer this lead to another team member
                </p>
              </div>
            )}

            {/* Mode of Contact - Required */}
            <div>
              <Label className="text-sm font-medium">Mode of Contact <span className="text-red-500">*</span></Label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-2">
                {CONTACT_MODES.map((mode) => {
                  const isSelected = formData.contact_mode === mode.value;
                  const IconComponent = mode.value === 'phone_call' ? Phone 
                    : mode.value === 'message' ? MessageSquare 
                    : mode.value === 'email' ? Mail 
                    : mode.value === 'video_call' ? Video 
                    : mode.value === 'no_contact' ? Ban
                    : Users;
                  return (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => handleFormChange('contact_mode', mode.value)}
                      className={`flex flex-col items-center p-2 rounded-lg border-2 transition-all ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50 text-blue-700' 
                          : 'border-slate-200 hover:border-slate-300 text-slate-600'
                      }`}
                      data-testid={`edit-contact-mode-${mode.value}`}
                    >
                      <IconComponent size={16} />
                      <span className="text-xs mt-1">{mode.label}</span>
                    </button>
                  );
                })}
              </div>
              {!formData.contact_mode && <p className="text-xs text-red-500 mt-1">Please select a mode of contact</p>}
            </div>

            <div>
              <Label htmlFor="edit_notes">Notes</Label>
              <Textarea
                id="edit_notes"
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                className="input-goldsands min-h-[80px]"
                data-testid="lead-notes"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => { setIsEditOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="btn-accent" data-testid="lead-submit">
                Update Lead
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <LeadViewDialog
        open={isViewOpen}
        onOpenChange={setIsViewOpen}
        lead={selectedLead}
        getOwnerName={getOwnerName}
        getOwnerInitials={getOwnerInitials}
      />

      {/* Closed Won Confirmation Dialog */}
      <AlertDialog open={!!closedWonConfirmation} onOpenChange={(open) => !open && setClosedWonConfirmation(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Confirm Closed Won
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to mark this lead as <strong>Closed Won</strong>?
              <br /><br />
              This confirms the lead is fully closed with deposit received. 
              <strong className="text-red-600"> This action cannot be undone</strong> — Closed Won is a terminal stage.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleClosedWonConfirm}
              className="bg-green-600 hover:bg-green-700"
            >
              Yes, Mark as Closed Won
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Activity History Modal */}
      <ActivityHistoryModal
        isOpen={isActivityModalOpen}
        onClose={() => {
          setIsActivityModalOpen(false);
          setActivityLeadId(null);
          setActivityLeadName('');
        }}
        leadId={activityLeadId}
        leadName={activityLeadName}
      />

      {/* Consultation Wizard Modal */}
      <ConsultationWizardModal
        isOpen={consultationWizard.open}
        onClose={() => setConsultationWizard({ open: false, lead: null })}
        lead={consultationWizard.lead}
        onSuccess={() => {
          fetchLeads();
          setConsultationWizard({ open: false, lead: null });
        }}
      />

      {/* Event RSVP Wizard Modal */}
      <EventRSVPWizardModal
        isOpen={eventRSVPWizard.open}
        onClose={() => setEventRSVPWizard({ open: false, lead: null })}
        lead={eventRSVPWizard.lead}
        onSuccess={() => {
          fetchLeads();
          setEventRSVPWizard({ open: false, lead: null });
        }}
      />

      {/* Stage Update Notes Modal */}
      <Dialog open={stageUpdateModal.open} onOpenChange={(open) => {
        if (!open) {
          setStageUpdateModal({ open: false, leadId: null, newStage: null, previousStage: null });
          setStageUpdateContactMode('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit size={18} className="text-blue-500" />
              Update Lead Stage
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="text-sm text-slate-600 mb-4">
              Moving lead from <Badge variant="outline" className={stageColors[stageUpdateModal.previousStage] || ''}>{STAGE_LABELS[stageUpdateModal.previousStage] || stageUpdateModal.previousStage || '—'}</Badge>
              {' '} to <Badge variant="outline" className={stageColors[stageUpdateModal.newStage] || ''}>{STAGE_LABELS[stageUpdateModal.newStage] || stageUpdateModal.newStage || '—'}</Badge>
            </div>
            
            {/* Mode of Contact - Required */}
            <div className="mb-4">
              <Label className="text-sm font-medium">Mode of Contact <span className="text-red-500">*</span></Label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-2">
                {CONTACT_MODES.map((mode) => {
                  const isSelected = stageUpdateContactMode === mode.value;
                  const IconComponent = mode.value === 'phone_call' ? Phone 
                    : mode.value === 'message' ? MessageSquare 
                    : mode.value === 'email' ? Mail 
                    : mode.value === 'video_call' ? Video 
                    : mode.value === 'no_contact' ? Ban
                    : Users;
                  return (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => setStageUpdateContactMode(mode.value)}
                      className={`flex flex-col items-center p-2 rounded-lg border-2 transition-all ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50 text-blue-700' 
                          : 'border-slate-200 hover:border-slate-300 text-slate-600'
                      }`}
                      data-testid={`contact-mode-${mode.value}`}
                    >
                      <IconComponent size={18} />
                      <span className="text-xs mt-1">{mode.label}</span>
                    </button>
                  );
                })}
              </div>
              {!stageUpdateContactMode && <p className="text-xs text-red-500 mt-1">Please select a mode of contact</p>}
            </div>
            
            <Label className="text-sm font-medium">Add a note <span className="text-red-500">*</span></Label>
            <Textarea
              value={stageUpdateNotes}
              onChange={(e) => setStageUpdateNotes(e.target.value)}
              placeholder="Describe the reason for this stage change..."
              className="mt-2 min-h-[100px]"
              data-testid="stage-update-notes"
            />
            
            {/* Quick Notes - User's Custom Notes (universal across all stages) */}
            <div className="mt-3">
              <p className="text-xs text-slate-500 mb-2">Your quick notes:</p>
              {userQuickNotes.length > 0 ? (
                <div className="space-y-1.5">
                  {userQuickNotes.map((qn) => (
                    <div key={qn.note_id} className="flex items-center gap-1 group" data-testid={`quick-note-${qn.note_id}`}>
                      {editingQuickNote === qn.note_id ? (
                        <div className="flex gap-1 flex-1">
                          <Input value={editQuickNoteText}
                            onChange={(e) => setEditQuickNoteText(e.target.value)}
                            className="text-xs h-7 flex-1"
                            onKeyDown={(e) => e.key === 'Enter' && handleEditQuickNote(qn.note_id)}
                            autoFocus />
                          <Button type="button" size="sm" className="h-7 px-2 text-xs bg-green-600 hover:bg-green-700"
                            onClick={() => handleEditQuickNote(qn.note_id)}>Save</Button>
                          <Button type="button" variant="ghost" size="sm" className="h-7 px-1.5 text-xs"
                            onClick={() => { setEditingQuickNote(null); setEditQuickNoteText(''); }}>Cancel</Button>
                        </div>
                      ) : (
                        <>
                          <Button type="button" variant="outline" size="sm"
                            className="text-xs h-7 px-2.5 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200 flex-shrink"
                            onClick={() => setStageUpdateNotes(prev => prev ? `${prev}\n${qn.note_text}` : qn.note_text)}>
                            {qn.note_text.length > 35 ? qn.note_text.substring(0, 35) + '...' : qn.note_text}
                          </Button>
                          <Button type="button" variant="ghost" size="sm"
                            className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => { setEditingQuickNote(qn.note_id); setEditQuickNoteText(qn.note_text); }}
                            title="Edit note">
                            <Edit size={12} />
                          </Button>
                          <Button type="button" variant="ghost" size="sm"
                            className="h-7 w-7 p-0 text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => handleDeleteQuickNote(qn.note_id)}
                            title="Delete note">
                            <Trash2 size={12} />
                          </Button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400 italic">No quick notes yet. Add your own below to speed up stage changes.</p>
              )}
            </div>
            
            {/* Add Custom Quick Note */}
            <div className="mt-3 pt-3 border-t">
              {showQuickNoteInput ? (
                <div className="flex gap-2">
                  <Input
                    value={newQuickNote}
                    onChange={(e) => setNewQuickNote(e.target.value)}
                    placeholder="Type your quick note..."
                    className="text-xs h-8"
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateQuickNote()}
                  />
                  <Button
                    type="button"
                    size="sm"
                    className="h-8 px-3 bg-green-600 hover:bg-green-700"
                    onClick={handleCreateQuickNote}
                    disabled={!newQuickNote.trim()}
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2"
                    onClick={() => { setShowQuickNoteInput(false); setNewQuickNote(''); }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-xs text-green-600 hover:text-green-700 hover:bg-green-50 h-7"
                  onClick={() => setShowQuickNoteInput(true)}
                >
                  <PlusCircle size={12} className="mr-1" /> Add your own quick note
                </Button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setStageUpdateModal({ open: false, leadId: null, newStage: null, previousStage: null });
              setStageUpdateContactMode('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleStageUpdateConfirm}
              disabled={!stageUpdateNotes.trim() || !stageUpdateContactMode}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Update Stage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Task Modal */}
      <LeadQuickTaskModal
        open={quickTaskModal.open}
        onClose={() => setQuickTaskModal({ open: false, lead: null })}
        lead={quickTaskModal.lead}
        onTaskCreated={() => {}}
      />

      {/* Download Wizard - Owner Only */}
      {isOwner && (
        <DownloadWizard
          open={downloadOpen}
          onOpenChange={setDownloadOpen}
          title="Download Leads"
          description="Select the columns you want to include in the leads export."
          columns={LEAD_DOWNLOAD_COLUMNS}
          onDownload={handleDownload}
          defaultSelected={LEAD_DOWNLOAD_COLUMNS.map(c => c.key)}
        />
      )}

      {/* Stage Settings Panel */}
      <StageSettingsPanel open={stageSettingsOpen} onOpenChange={(open) => {
        setStageSettingsOpen(open);
        if (!open) fetchStageConfig();
      }} />

      {/* Opportunity Fields Modal */}
      <Dialog open={opportunityModal.open} onOpenChange={(open) => !open && setOpportunityModal({ open: false, leadId: null, targetStage: null, previousStage: null })}>
        <DialogContent className="sm:max-w-md" data-testid="opportunity-fields-modal">
          <DialogHeader>
            <DialogTitle>Opportunity Details Required</DialogTitle>
            <DialogDescription>
              This lead is moving to an Opportunity stage. Please fill in the required fields.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Deal Value (USD) *</Label>
              <Input type="number" value={opportunityForm.deal_value}
                onChange={(e) => setOpportunityForm(p => ({ ...p, deal_value: e.target.value }))}
                placeholder="e.g. 50000" data-testid="opp-deal-value" />
            </div>
            <div>
              <Label>Estimated Cash Collection (USD) *</Label>
              <Input type="number" value={opportunityForm.estimated_cash_collection}
                onChange={(e) => setOpportunityForm(p => ({ ...p, estimated_cash_collection: e.target.value }))}
                placeholder="e.g. 45000" data-testid="opp-cash-collection" />
            </div>
            <div>
              <Label>Product Interest *</Label>
              <Select value={opportunityForm.product_interest}
                onValueChange={(v) => setOpportunityForm(p => ({ ...p, product_interest: v }))}>
                <SelectTrigger data-testid="opp-product"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>
                  {PRODUCTS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpportunityModal({ open: false, leadId: null, targetStage: null, previousStage: null })}>Cancel</Button>
            <Button onClick={handleOpportunitySubmit}
              disabled={!opportunityForm.deal_value || !opportunityForm.product_interest || !opportunityForm.estimated_cash_collection}
              data-testid="opp-submit">
              Save & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
